import { useState, useEffect, useCallback, useRef } from 'react';
import { useImpersonation } from '@center-technologies/shared-api-clients';
import { useApiClientsOrNull } from '../context/ApiClientsContext';
import { ChannelItemType } from 'types/channel';
import {
  listApproaches,
  listSurfaceTemplates,
  listSchemas,
  listContexts,
  listArcTemplatesForSearch,
  listOrbitTemplatesForSearch,
  listMetaspacesForSearch,
  ListApproachesResponse,
  ListSurfaceTemplatesResponse,
} from '../api/marketplaceApi';
import { getLocalizedText } from '../utils/getLocalizedText';

export interface SearchContentItem {
  id: string;
  name: string;
  description?: string;
  type: ChannelItemType;
  tags?: string[];
}

export interface UseSearchContentResult {
  results: SearchContentItem[];
  total: number;
  loading: boolean;
  error: string | null;
  search: (query: string) => void;
  setContentType: (type: ChannelItemType | 'all') => void;
  contentType: ChannelItemType | 'all';
}

const DEBOUNCE_MS = 300;

/**
 * Collapse a per-type fetch failure to "no results of this type", but only for a 404.
 *
 * These list endpoints legitimately 404 when the org owns none of that type, so a blanket
 * `.catch(() => [])` looks reasonable — and it is how every one of these fetchers used to be
 * written. The problem is that it renders a 401, 403 or 500 as "you have none of these", which
 * is indistinguishable from the truthful empty case and sends the user looking for missing data
 * instead of a broken session or a failing service.
 *
 * Non-404s still resolve to `[]` on purpose: this hook aggregates several independent type
 * searches, and one failing type should not blank the whole result set. The difference is that
 * the failure is now visible in the console with its type and status rather than being erased.
 */
function emptyOnNotFound<T>(itemType: string, promise: Promise<T[]>): Promise<T[]> {
  return promise.catch((err: unknown) => {
    const status = (err as { response?: { status?: number } })?.response?.status;
    if (status !== 404) {
      console.warn(
        `[useSearchContent] ${itemType} search failed (status ${status ?? 'unknown'}) — omitting this type from results`,
        err
      );
    }
    return [] as T[];
  });
}

export function useSearchContent(): UseSearchContentResult {
  const apiClients = useApiClientsOrNull();
  const { impersonationDetails } = useImpersonation();
  const organizationId = impersonationDetails?.organizationId;

  const [results, setResults] = useState<SearchContentItem[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [query, setQuery] = useState('');
  const [contentType, setContentType] = useState<ChannelItemType | 'all'>('all');

  const debounceRef = useRef<ReturnType<typeof setTimeout>>();

  const search = useCallback((term: string) => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }
    debounceRef.current = setTimeout(() => {
      setQuery(term);
      setResults([]);
    }, DEBOUNCE_MS);
  }, []);

  useEffect(() => {
    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (!apiClients || !organizationId) {
      return;
    }

    let cancelled = false;

    const doSearch = async () => {
      setLoading(true);
      setError(null);

      try {
        const allResults: SearchContentItem[] = [];

        const shouldFetchApproaches =
          contentType === 'all' || contentType === 'approach';
        const shouldFetchTemplates =
          contentType === 'all' || contentType === 'surface_template';
        const shouldFetchSchemas = contentType === 'all' || contentType === 'schema';
        const shouldFetchContexts = contentType === 'all' || contentType === 'context';
        const shouldFetchArcTemplates =
          contentType === 'all' || contentType === 'arc_template';
        const shouldFetchOrbitTemplates =
          contentType === 'all' || contentType === 'orbit_template';
        const shouldFetchMetaspaces = contentType === 'all' || contentType === 'metaspace';

        const promises: Promise<void>[] = [];

        if (shouldFetchApproaches) {
          promises.push(
            listApproaches(apiClients.schemaClient, { limit: 50 })
              .then((response: ListApproachesResponse) => {
                const approaches = response.approaches || [];
                for (const approach of approaches) {
                  const name = approach.name?.default || approach.system_name || '';
                  if (!query || name.toLowerCase().includes(query.toLowerCase())) {
                    allResults.push({
                      id: approach.id,
                      name,
                      description: approach.description?.default,
                      type: 'approach',
                      tags: approach.tags,
                    });
                  }
                }
              })
          );
        }

        if (shouldFetchTemplates) {
          promises.push(
            listSurfaceTemplates(apiClients.schemaClient, { limit: 50 })
              .then((response: ListSurfaceTemplatesResponse) => {
                const templates = response.surface_templates || [];
                for (const template of templates) {
                  const name = template.name?.default || template.system_name || '';
                  if (!query || name.toLowerCase().includes(query.toLowerCase())) {
                    allResults.push({
                      id: template.id,
                      name,
                      description: template.description?.default,
                      type: 'surface_template',
                    });
                  }
                }
              })
          );
        }

        // The five template-supertype list endpoints (schemas, contexts,
        // arc-templates, orbit-templates, metaspaces) have no server-side
        // search/limit param and 404 when the org has none of that type —
        // treat a 404 as "no results of this type", not a hard failure.
        // See `emptyOnNotFound`: anything other than a 404 is logged rather
        // than silently rendered as an empty result set.
        if (shouldFetchSchemas) {
          promises.push(
            emptyOnNotFound('schema', listSchemas(apiClients.schemaClient))
              .then((schemas) => {
                for (const schema of schemas) {
                  const name = getLocalizedText(schema.name) || schema.system_name || '';
                  if (!query || name.toLowerCase().includes(query.toLowerCase())) {
                    allResults.push({
                      id: schema.schema_id,
                      name,
                      description: getLocalizedText(schema.description) || undefined,
                      type: 'schema',
                    });
                  }
                }
              })
          );
        }

        if (shouldFetchContexts) {
          promises.push(
            emptyOnNotFound('context', listContexts(apiClients.schemaClient))
              .then((contexts) => {
                for (const context of contexts) {
                  const name = getLocalizedText(context.name) || context.system_name || '';
                  if (!query || name.toLowerCase().includes(query.toLowerCase())) {
                    allResults.push({
                      id: context.id,
                      name,
                      description: getLocalizedText(context.description) || undefined,
                      type: 'context',
                    });
                  }
                }
              })
          );
        }

        if (shouldFetchArcTemplates) {
          promises.push(
            emptyOnNotFound('arc_template', listArcTemplatesForSearch(apiClients.schemaClient))
              .then((arcTemplates) => {
                for (const arcTemplate of arcTemplates) {
                  const name = getLocalizedText(arcTemplate.name) || arcTemplate.system_name || '';
                  if (!query || name.toLowerCase().includes(query.toLowerCase())) {
                    allResults.push({
                      id: arcTemplate.id,
                      name,
                      type: 'arc_template',
                    });
                  }
                }
              })
          );
        }

        if (shouldFetchOrbitTemplates) {
          promises.push(
            emptyOnNotFound('orbit_template', listOrbitTemplatesForSearch(apiClients.schemaClient))
              .then((orbitTemplates) => {
                for (const orbitTemplate of orbitTemplates) {
                  const name =
                    getLocalizedText(orbitTemplate.name) || orbitTemplate.system_name || '';
                  if (!query || name.toLowerCase().includes(query.toLowerCase())) {
                    allResults.push({
                      id: orbitTemplate.id,
                      name,
                      type: 'orbit_template',
                    });
                  }
                }
              })
          );
        }

        if (shouldFetchMetaspaces) {
          promises.push(
            // Wrapped on `.metaspaces` rather than the response: this endpoint returns an
            // envelope, so the shared helper operates on the array it carries.
            emptyOnNotFound(
              'metaspace',
              listMetaspacesForSearch(apiClients.schemaClient).then(
                (response) => response.metaspaces || []
              )
            )
              .then((metaspaces) => {
                for (const metaspace of metaspaces) {
                  const name = getLocalizedText(metaspace.name) || metaspace.system_name || '';
                  if (!query || name.toLowerCase().includes(query.toLowerCase())) {
                    allResults.push({
                      id: metaspace.metaspace_id,
                      name,
                      description: getLocalizedText(metaspace.description) || undefined,
                      type: 'metaspace',
                    });
                  }
                }
              })
          );
        }

        await Promise.all(promises);

        if (!cancelled) {
          setResults(allResults);
          setTotal(allResults.length);
        }
      } catch (err) {
        if (!cancelled) {
          const message = err instanceof Error ? err.message : 'Search failed';
          setError(message);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    doSearch();

    return () => {
      cancelled = true;
    };
  }, [apiClients, organizationId, query, contentType]);

  return {
    results,
    total,
    loading,
    error,
    search,
    setContentType,
    contentType,
  };
}
